import { useCallback, useMemo, useState } from 'react';
import chatService from 'services/chat';
import { CHAT_PAGE_SIZE, NEW_THREAD_VALUE, THREAD_SUMMARY_LIMIT } from 'utils/chat/chatConstants';
import {
    buildThreadFilter,
    buildThreadSummaryFromChat,
    createLocalChatShell,
    getThreadScopeLabel,
    getThreadTitle,
    upsertThreadSummary,
} from './useInstructorHugoChatHelpers';

const useInstructorHugoThreads = ({ auth, chat, chatRef, scope, setChat, t }) => {
    const [threadSummaries, setThreadSummaries] = useState([]);
    const [isLoadingThreads, setIsLoadingThreads] = useState(false);
    const [isSwitchingThread, setIsSwitchingThread] = useState(false);
    const selectedThreadValue = chat.id || NEW_THREAD_VALUE;

    const loadThreadSummaries = useCallback(async () => {
        if (!auth?.owner_account_id) {
            setThreadSummaries([]);
            return [];
        }

        setIsLoadingThreads(true);
        try {
            const summaries = await chatService.listChats(buildThreadFilter({ auth }), {
                includeMessages: false,
                includeNames: true,
                orderBy: 'modified_on desc',
                top: THREAD_SUMMARY_LIMIT,
            });
            const nextSummaries = summaries || [];
            setThreadSummaries(nextSummaries);

            return nextSummaries;
        } catch {
            setThreadSummaries([]);
            return [];
        } finally {
            setIsLoadingThreads(false);
        }
    }, [auth]);

    const createThreadShell = useCallback(() => {
        setChat(createLocalChatShell({ auth, scope }));
    }, [auth, scope, setChat]);

    const switchThread = useCallback(
        async (threadId) => {
            if (!threadId || threadId === NEW_THREAD_VALUE) {
                createThreadShell();
                return;
            }

            setIsSwitchingThread(true);
            try {
                const nextChat = await chatService.getChat(threadId, {
                    messages_top: CHAT_PAGE_SIZE,
                });
                if (!nextChat?.id) return;
                setChat(nextChat);
            } finally {
                setIsSwitchingThread(false);
            }
        },
        [createThreadShell, setChat]
    );

    const bindLocalChatToPersistedChat = useCallback(
        ({ chatId: persistedChatId }) => {
            if (!persistedChatId) return;

            const currentChat = chatRef.current;
            if (currentChat && !currentChat.id) {
                setThreadSummaries((currentSummaries) =>
                    upsertThreadSummary(
                        currentSummaries,
                        buildThreadSummaryFromChat({
                            chat: currentChat,
                            chatId: persistedChatId,
                        })
                    )
                );
            }

            setChat((currentChat) => {
                if (!currentChat) return currentChat;
                if (currentChat?.id) return currentChat;

                return {
                    ...currentChat,
                    id: persistedChatId,
                    is_processing: true,
                };
            });
            loadThreadSummaries();
        },
        [chatRef, loadThreadSummaries, setChat]
    );

    const onDeleteThread = useCallback(
        async (threadId) => {
            if (!threadId) return;
            try {
                await chatService.deleteChat(threadId);
                setThreadSummaries((currentSummaries) => currentSummaries.filter((thread) => thread.id !== threadId));

                if (chat.id === threadId) {
                    createThreadShell();
                }
            } catch (error) {
                console.error('Failed to delete thread', error);
            }
        },
        [chat.id, createThreadShell]
    );

    const threadListAdapter = useMemo(
        () => ({
            threadId: chat.id || undefined,
            isLoading: isLoadingThreads,
            threads: threadSummaries.map((thread) => ({
                id: thread.id,
                title: getThreadTitle(thread),
                status: 'regular',
            })),
            onSwitchToNewThread: createThreadShell,
            onSwitchToThread: switchThread,
        }),
        [chat.id, createThreadShell, isLoadingThreads, switchThread, threadSummaries]
    );

    const threadSwitcher = useMemo(
        () => ({
            disabled: isSwitchingThread,
            isLoading: isLoadingThreads,
            items: threadSummaries.map((thread) => ({
                id: thread.id,
                label: getThreadTitle(thread),
                scopeLabel: getThreadScopeLabel(thread, t),
            })),
            newThreadValue: NEW_THREAD_VALUE,
            selectedValue: selectedThreadValue,
            onSelect: switchThread,
            onStartNew: createThreadShell,
        }),
        [createThreadShell, isLoadingThreads, isSwitchingThread, selectedThreadValue, switchThread, t, threadSummaries]
    );

    return {
        bindLocalChatToPersistedChat,
        loadThreadSummaries,
        onDeleteThread,
        threadListAdapter,
        threadSwitcher,
    };
};

export default useInstructorHugoThreads;
